"use client";
import { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle, Clock, Truck, MapPinned, X, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// Helper function to format date
const formatDate = (date) => {
  const options = { year: "numeric", month: "long", day: "numeric" };
  return new Date(date).toLocaleDateString("en-IN", options);
};

export default function CustomerOrder() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [returnModal, setReturnModal] = useState(false);
const [returnData, setReturnData] = useState({
  order_code: "",
  order_detail_id: "",
  product_name: "",
  type: "return", // return | exchange
  return_reason: "",
  customer_note: "",
});

  // ✅ Get token & userId from localStorage
  const authToken = localStorage.getItem("authToken");
  const authUserId = localStorage.getItem("authUserId");
  const navigate = useNavigate();

  // ✅ Redirect if not logged in
  useEffect(() => {
    if (!authToken || !authUserId) {
      console.error("Auth token or userId not found in localStorage");
      navigate("/login");
    }
  }, [authToken, authUserId, navigate]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError("");

        const token = localStorage.getItem("authToken");
        if (!token) {
          setError("User not logged in. Please login first.");
          setOrders([]);
          setLoading(false);
          return;
        }

        const response = await axios.get(
          "https://tyka.premierhostings.com/beta/api/get-user-orders",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log("response", response.data);

        if (response?.data && Array.isArray(response?.data?.data)) {
          setOrders(response.data.data);
        } else {
          setError("No orders found.");
          setOrders([]);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError("Failed to fetch orders. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-sm font-semibold">
            <Clock size={16} /> Pending
          </span>
        );
      case "shipped":
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
            <Truck size={16} /> Shipped
          </span>
        );
      case "reached":
      case "delivered":
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm font-semibold">
            <CheckCircle size={16} /> Delivered
          </span>
        );
      case "track":
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold">
            <MapPinned size={16} /> Tracking
          </span>
        );  
        case "return":
  return (
    <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-sm font-semibold">
      <RotateCcw size={16} /> Returned
    </span>
  );
      default:
        return (
          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-gray-200 text-gray-700 text-sm font-semibold">
            {status || "Unknown"}
          </span>
        );
    }
  };

const handleReturnRequest = async () => {
  try {
    const token = localStorage.getItem("authToken");

    const response = await axios.post(
      "https://tyka.premierhostings.com/beta/api/product-returns",
      {
        order_code: returnData.order_code,
        order_detail_id: returnData.order_detail_id,
        return_reason: returnData.return_reason,
        customer_note: returnData.customer_note,
        type: returnData.type,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.data.success) {
      Swal.fire({
        icon: "success",
        title: "Success",
        text: response.data.message || "Request submitted successfully.",
      });

      setReturnModal(false);
    } else {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: response.data.message || "Something went wrong.",
      });
    }
  } catch (error) {
    console.log(error);

    const message =
      error.response?.data?.message || "Unable to submit request.";

    Swal.fire({
      icon: "error",
      title: "Failed",
      text: message,
    });
  }
};

  return (
    <div className="p-6 md:p-10 bg-gray-50 min-h-screen">
      {/* Heading */}
      <h1 className="text-3xl font-bold mb-8 text-center">📦 My Orders</h1>

      {/* Loading State */}
      {loading && (
        <p className="text-center text-gray-600 ">
          Loading your orders...
        </p>
      )}

      {/* Error State */}
      {error && !loading && (
        <p className="text-center text-red-500 font-medium">{error}</p>
      )}

      {/* Orders List */}
      {!loading && !error && (
        <div className="space-y-6">
          {orders.length > 0 ? (
  orders.map((order) => {

    const groupedOrderDetails = Object.values(
      order.order_details.data.reduce((acc, item) => {
        if (item.bundle) {
          const key = `bundle-${item.bundle.id}`;

          if (!acc[key]) {
            acc[key] = {
              id: key,
              isBundle: true,
              bundle: item.bundle,
              items: [],
            };
          }

          acc[key].items.push(item);
        } else {
          acc[`product-${item.id}`] = {
            ...item,
            isBundle: false,
          };
        }

        return acc;
      }, {})
    );
    console.log("groupedOrderDetails", groupedOrderDetails);

    return (
      <div
        key={order.id}
        className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all p-5"
      >
                        {/* Order Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 pb-4 border-b ">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-800 ">
                      Order #{order.order_code}
                    </h2>
                    <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-600">
                      <p>
                        <span className="font-medium">Order Date:</span>{" "}
                        {formatDate(order.order_date)}
                      </p>
                      <p>
                        <span className="font-medium">Payment Status:</span>{" "}
                        {order.payment_status}
                      </p>
                      <p style={{ marginTop: "-6px" }}>
                        <span className="font-medium">Total Amount:</span>{" "}
                        <span className="text-lg font-bold text-red-600 ">
                          ₹{Number(order.total_amount).toLocaleString("en-IN")}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 md:mt-0 flex items-center gap-2">
                    {getStatusBadge(order.order_status)}
                  {getStatusBadge('track')}
                  </div>
                </div>

                <div className="space-y-4">
{groupedOrderDetails.map((orderDetail) => {
  // Normal Product
  if (!orderDetail.isBundle) {
    return (
                    <div
                      key={orderDetail.id}
                      className="grid grid-cols-1 md:grid-cols-12 gap-4 p-4 bg-gray-50 rounded-xl items-center"
                    >
                      {/* Product Image */}
                      <div className="md:col-span-2 flex justify-center">
                        <div className="w-24 h-24 rounded-lg border bg-white flex items-center justify-center overflow-hidden">
                          <img
                            src={orderDetail.products.default_image || "/placeholder.svg"}
                            alt={orderDetail.products.name}
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      </div>

                      {/* Product Details */}
                      <div className="md:col-span-8">
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-semibold text-gray-800">
                            {orderDetail.products.name}
                          </h3>

                          <X size={14} className="text-gray-500" />

                          <span className="text-lg font-medium text-gray-700">
                            {orderDetail.quantity}
                          </span>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-3 text-sm">
                          {/* Color */}
                          <div>
                            <p className="text-gray-500">Color</p>

                            <div className="flex items-center gap-2 mt-1">
                              {orderDetail.colors.hexa_code_2 ? (
                                <div className="relative w-5 h-5 rounded-full border overflow-hidden">
                                  <div
                                    className="absolute left-0 top-0 w-1/2 h-full"
                                    style={{
                                      backgroundColor: orderDetail.colors.hexa_code,
                                    }}
                                  />
                                  <div
                                    className="absolute right-0 top-0 w-1/2 h-full"
                                    style={{
                                      backgroundColor: orderDetail.colors.hexa_code_2,
                                    }}
                                  />
                                </div>
                              ) : (
                                <span
                                  className="w-5 h-5 rounded-full border"
                                  style={{
                                    backgroundColor: orderDetail.colors.hexa_code,
                                  }}
                                />
                              )}

                              <span>{orderDetail.colors.name}</span>
                            </div>
                          </div>

                          {/* Size */}
                          <div>
                            <p className="text-gray-500">Size</p>
                            <p className="font-medium">{orderDetail.sizes.name}</p>
                          </div>

                          {/* Price */}
                          <div>
                            <p className="text-gray-500">Price</p>
                            <p className="font-bold text-red-600">
                              ₹{Number(orderDetail.price).toLocaleString("en-IN")}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="md:col-span-2 flex justify-end">
                        <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium">
                          {order.order_status}
                        </span>
                                  {!["cancelled"].includes(order.order_status) && (
                  <button
                    onClick={() => {
                      setReturnData({
                        order_code: order.order_code,
                        order_detail_id: orderDetail.id,
                        product_name: orderDetail.products.name,
                        type: "return",
                        return_reason: "",
                        customer_note: "",
                      });

                      setReturnModal(true);
                    }}
                    className="flex items-center gap-1 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-sm font-semibold hover:bg-orange-200"
                  >
                    <RotateCcw size={16} />
                    Return
                  </button>
                )}
                      </div>
                    </div>
    );
  }

  // Bundle
  return (
<div className="border rounded-xl p-5 bg-gray-50">

  {/* Bundle Header */}
  <div className="flex justify-between items-center mb-5 border-b pb-3">
    <div>
      <h2 className="text-xl font-bold">
        Bundle: {orderDetail.bundle.name}
      </h2>
      <p className="text-sm text-gray-500">
        Offer Price: ₹{orderDetail.bundle.offer_price}
      </p>
    </div>

    <button
      onClick={() => {
        setReturnData({
          order_code: order.order_code,
          order_detail_id: orderDetail.items[0].id,
          product_name: orderDetail.bundle.name,
          type: "return",
          return_reason: "",
          customer_note: "",
        });

        setReturnModal(true);
      }}
      className="flex items-center gap-1 px-3 py-2 rounded-full bg-orange-100 text-orange-700"
    >
      <RotateCcw size={16} />
      Return
    </button>
  </div>

  {/* Bundle Products */}
<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
  {orderDetail.items.map((item) => (
    <div
      key={item.id}
      className="flex gap-4 rounded-lg p-4 bg-white"
    >
      {/* Image */}
      <div className="w-24 h-24 flex-shrink-0 rounded-lg border bg-white flex items-center justify-center overflow-hidden">
        <img
          src={item.products?.default_image || "/placeholder.svg"}
          alt={item.products?.name}
          className="w-full h-full object-contain"
        />
      </div>

      {/* Content */}
      <div className="flex-1">
        <h3 className="text-lg font-semibold text-gray-800">
          {item.products?.name}
        </h3>

        <div className="grid grid-cols-2 gap-3 mt-3 text-sm">

          <div>
            <p className="text-gray-500">Color</p>
            <p>{item.colors?.name}</p>
          </div>

          <div>
            <p className="text-gray-500">Size</p>
            <p>{item.sizes?.name}</p>
          </div>

        </div>
      </div>
    </div>
  ))}
</div>
</div>
  );
})}
                </div>
      </div>
    );
  })
) : (
  <p className="text-center text-gray-600">
    You have no orders yet.
  </p>
)}
        </div>
      )}
      {returnModal && (

  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
  <div className="bg-white rounded-xl w-full max-w-lg max-h-[90vh] flex flex-col">

    {/* Header */}
    <div className="flex justify-between items-center p-5 border-b">
      <h2 className="text-xl font-bold">Return / Exchange</h2>
      <button onClick={() => setReturnModal(false)}>
        <X size={20} />
      </button>
    </div>

    {/* Body */}
    <div className="flex-1 overflow-y-auto p-5 space-y-4">
            <div className="space-y-4">

        <div>
          <label className="text-sm font-medium">
            Order Code
          </label>

          <input
            value={returnData.order_code}
            readOnly
            className="w-full border rounded-lg p-2 bg-gray-100"
          />
        </div>

        <div>
          <label className="text-sm font-medium">
            Product
          </label>

          <input
            value={returnData.product_name}
            readOnly
            className="w-full border rounded-lg p-2 bg-gray-100"
          />
        </div>

        <div>
          <label className="text-sm font-medium">
            Request Type
          </label>

          <select
            value={returnData.type}
            onChange={(e) =>
              setReturnData({
                ...returnData,
                type: e.target.value,
              })
            }
            className="w-full border rounded-lg p-2"
          >
            <option value="return">Return</option>
            <option value="exchange">Exchange</option>
          </select>
        </div>

        <div>
          <label className="text-sm font-medium">
            Return Reason
          </label>

          <input
            value={returnData.return_reason}
            onChange={(e) =>
              setReturnData({
                ...returnData,
                return_reason: e.target.value,
              })
            }
            placeholder="Size issue"
            className="w-full border rounded-lg p-2"
          />
        </div>

        <div>
          <label className="text-sm font-medium">
            Customer Note
          </label>

          <textarea
            rows={4}
            value={returnData.customer_note}
            onChange={(e) =>
              setReturnData({
                ...returnData,
                customer_note: e.target.value,
              })
            }
            placeholder="Need another size..."
            className="w-full border rounded-lg p-2"
          />
        </div>

      </div>
    </div>

    {/* Footer */}
    <div className="flex justify-end gap-3 p-5 border-t">
      <button
        onClick={() => setReturnModal(false)}
        className="px-4 py-2 border rounded-lg"
      >
        Cancel
      </button>

      <button
        onClick={handleReturnRequest}
        className="px-4 py-2 bg-red-600 text-white rounded-lg"
      >
        Submit
      </button>
    </div>

  </div>
</div>
)}
    </div>
  );
}
